import React, {
  ComponentProps,
  FC,
  PropsWithChildren,
  useEffect,
  useState,
} from "react";
import { Helmet } from "react-helmet";
import { useGate } from "statsig-react";
import { Placeholder } from "kanopi-components/Placeholder";
import { Redirect } from "@reach/router";
import { Block } from "baseui/block";
import { Button } from "baseui/button";
import { SummaryCard } from "./components/SummaryCard";
import { FormControl, Input } from "kanopi-components/Input";
import { useAPI } from "kw/api/useAPI";
import { GraphQLTypes } from "kw/api/zeus";
import { useUser } from "kw/auth/useUser";
import { toaster } from "kanopi-components/ToastContainer";

type ConfigInputProps = {
  label: string;
  defaultValue?: string;
} & ComponentProps<typeof Input>;

const HIDDEN_CLIENT_SECRET = "********";

function ConfigInput({ label, defaultValue, ...rest }: ConfigInputProps) {
  const [value, setValue] = useState<string | undefined>(defaultValue);

  return (
    <Block width="100%">
      <FormControl label={label}>
        <Input
          type="text"
          onChange={(e) => setValue(e.target.value)}
          value={value}
          overrides={{
            Root: {
              style: {
                flexBasis: "50%",
              },
            },
          }}
          {...rest}
        />
      </FormControl>
    </Block>
  );
}

function ConfigRow({ children }: PropsWithChildren) {
  return (
    <Block
      display="flex"
      justifyContent="space-between"
      width={"100%"}
      overrides={{
        Block: {
          style: {
            gap: "24px",
          },
        },
      }}
    >
      {children}
    </Block>
  );
}

export const GlobalSettingsPage: FC = () => {
  const user = useUser();
  const { value, isLoading } = useGate("k-166-admin-sso-config");
  const { query, mutation, loading } = useAPI();
  const [config, setConfig] = useState<GraphQLTypes["SsoConfigurationType"] | null>(
    null
  );
  const [isValuesLoading, setIsValuesLoading] = useState(true);

  useEffect(() => {
    query(
      {
        ssoConfiguration: {
          clientIdKey: true,
          isClientSecretKeySet: true,
          loginUrl: true,
          logoutUrl: true,
          successRedirectUrl: true,
          errorRedirectUrl: true,
          callbackUrl: true,
        },
      },
      // @ts-expect-error TS_FIXME get types working for useAPI hook
      (response: { ssoConfiguration: GraphQLTypes["SsoConfigurationType"] }) => {
        setConfig(response.ssoConfiguration);
        setIsValuesLoading(false);
      }
    );
  }, []);

  if (isLoading || isValuesLoading) {
    return <Placeholder loading />;
  }

  if (!value) {
    return <Redirect to="/admin/" noThrow />;
  }

  return (
    <>
      <Helmet>
        <title>Settings - {user?.organization?.name}</title>
      </Helmet>
      <SummaryCard title="SSO Configuration">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            const entries = Object.fromEntries(formData);
            if (entries.clientSecretKey === HIDDEN_CLIENT_SECRET) {
              delete entries.clientSecretKey;
            }

            mutation(
              {
                updateSsoConfiguration: [
                  {
                    input: entries,
                  },
                  {
                    clientIdKey: true,
                    loginUrl: true,
                    logoutUrl: true,
                    successRedirectUrl: true,
                    errorRedirectUrl: true,
                    callbackUrl: true,
                  },
                ],
              },
              // @ts-expect-error TS_FIXME get types working for useAPI hook
              (response: {
                ssoConfiguration: GraphQLTypes["SsoConfigurationType"];
              }) => {
                setConfig(response.ssoConfiguration);
                toaster({
                  kind: "POSITIVE",
                  content: "SSO values have been updated",
                  key: "sso-config-update-success-toast",
                });
              }
            );
          }}
        >
          <Block
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            overrides={{
              Block: {
                style: {
                  gap: "24px",
                },
              },
            }}
          >
            <ConfigRow>
              <ConfigInput
                label="Client Id Key"
                name="clientIdKey"
                defaultValue={config?.clientIdKey}
              />
              <ConfigInput
                label="Client Secret Key"
                name="clientSecretKey"
                type="password"
                defaultValue={config?.isClientSecretKeySet ? HIDDEN_CLIENT_SECRET : ""}
              />
            </ConfigRow>
            {user?.isStaff ? (
              <>
                <ConfigRow>
                  <ConfigInput
                    label="Login Url"
                    name="loginUrl"
                    type="url"
                    defaultValue={config?.loginUrl}
                  />
                  <ConfigInput
                    label="Logout Url"
                    name="logoutUrl"
                    type="url"
                    defaultValue={config?.logoutUrl}
                  />
                </ConfigRow>
                <ConfigRow>
                  <ConfigInput
                    label="Success Redirect Url"
                    name="successRedirectUrl"
                    type="url"
                    defaultValue={config?.successRedirectUrl}
                  />
                  <ConfigInput
                    label="Error Redirect Url"
                    name="errorRedirectUrl"
                    type="url"
                    defaultValue={config?.errorRedirectUrl}
                  />
                </ConfigRow>
                <ConfigRow>
                  <ConfigInput
                    label="Callback Url"
                    name="callbackUrl"
                    type="url"
                    defaultValue={config?.callbackUrl}
                  />
                </ConfigRow>
              </>
            ) : null}
            {/* using the isLoading prop looks janky when the spinner pops in/out because the mutation happens quickly
          changing the colour of the button to indicate loading is a better UX */}
            <Button type="submit" isSelected={loading}>
              Save
            </Button>
          </Block>
        </form>
      </SummaryCard>
    </>
  );
};
